import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Hoş geldiniz');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  if (user) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight">
              Sosyal.Team
            </h1>
            <p className="text-[#737373] mt-3 font-ibm text-sm leading-relaxed">
              Çok kiracılı içerik zekası platformu.
              <br />
              Marka uyumlu içerikler üretin.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A3A3A3] font-ibm text-sm">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@sosyal.team"
                required
                data-testid="login-email-input"
                className="bg-[#141414] border-[#262626] text-white placeholder:text-[#525252] h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A3A3A3] font-ibm text-sm">
                Şifre
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Şifrenizi girin"
                required
                data-testid="login-password-input"
                className="bg-[#141414] border-[#262626] text-white placeholder:text-[#525252] h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-white text-black hover:bg-[#E5E5E5] h-11 font-ibm font-medium active:scale-[0.98] transition-transform duration-100"
            >
              {loading ? 'Giriş yapılıyor...' : (
                <span className="flex items-center justify-center gap-2">
                  Giriş Yap <ArrowRight size={16} />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-10 p-4 bg-[#141414] border border-[#262626] rounded-lg">
            <p className="text-xs text-[#737373] font-ibm mb-3">Demo hesapları</p>
            <div className="space-y-1.5 text-xs font-jetbrains text-[#A3A3A3]">
              <p>Super Admin: admin@sosyal.team / admin123</p>
              <p>Tenant Admin: admin@democorp.com / demo123</p>
              <p>Editor: editor@democorp.com / editor123</p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block w-1/2 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FACC15]/5 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-64 h-64 rounded-full border border-[#262626] opacity-20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border border-[#FACC15]/10" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-[#FACC15]/5" />
        </div>
      </div>
    </div>
  );
}
